/**
 * Onboarding Metrics Dashboard
 * Analytics and performance metrics for onboarding operations
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  Progress,
  Select,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  useToast,
  VStack,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  MdPeople,
  MdCheckCircle,
  MdStar,
  MdWarning,
  MdTrendingUp,
  MdTrendingDown,
} from 'react-icons/md';
import { getOnboardingStats, getOnboardings } from '../../../services/onboardingManagement';
import type { OnboardingStats, Onboarding, OnboardingStep } from '../../../types/onboarding';
import { getStepDisplayName } from '../../../types/onboarding';

export function OnboardingMetrics() {
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<OnboardingStats | null>(null);
  const [onboardings, setOnboardings] = useState<Onboarding[]>([]);
  const [dateRange, setDateRange] = useState<'7' | '30' | '90' | 'all'>('30');

  // Load data
  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, onboardingsData] = await Promise.all([
        getOnboardingStats(),
        getOnboardings(),
      ]);

      setStats(statsData);
      setOnboardings(onboardingsData.onboardings);
    } catch (error) {
      toast({
        title: 'Error loading metrics',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading || !stats) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  // Calculate time-to-value distribution
  const getTimeToValueDistribution = () => {
    const completedOnboardings = onboardings.filter(
      (o) => o.status === 'completed' && o.timeToValue
    );

    const ranges = [
      { label: '0-14 days', min: 0, max: 14, count: 0, color: 'green' },
      { label: '15-21 days', min: 15, max: 21, count: 0, color: 'blue' },
      { label: '22-30 days', min: 22, max: 30, count: 0, color: 'yellow' },
      { label: '31+ days', min: 31, max: Infinity, count: 0, color: 'red' },
    ];

    completedOnboardings.forEach((o) => {
      const ttv = o.timeToValue!;
      const range = ranges.find((r) => ttv >= r.min && ttv <= r.max);
      if (range) range.count++;
    });

    return ranges;
  };

  // Calculate NPS trend (simplified - would need historical data for real trend)
  const getNPSTrend = () => {
    const withNPS = onboardings.filter((o) => o.npsScore !== undefined);
    if (withNPS.length === 0) return 0;

    const recentCount = Math.min(5, withNPS.length);
    const recent = withNPS.slice(-recentCount);
    const older = withNPS.slice(0, -recentCount);

    if (older.length === 0) return 0;

    const recentAvg = recent.reduce((sum, o) => sum + o.npsScore!, 0) / recent.length;
    const olderAvg = older.reduce((sum, o) => sum + o.npsScore!, 0) / older.length;

    return recentAvg - olderAvg;
  };

  // Get blocker trend
  const getBlockerTrend = () => {
    const total = stats.activeBlockers + stats.resolvedBlockers;
    if (total === 0) return 0;

    const resolutionRate = (stats.resolvedBlockers / total) * 100;
    return resolutionRate >= 80 ? 1 : resolutionRate >= 60 ? 0 : -1;
  };

  const timeToValueDistribution = getTimeToValueDistribution();
  const npsTrend = getNPSTrend();
  const blockerTrend = getBlockerTrend();

  // Calculate funnel data
  const getFunnelData = () => {
    const steps: OnboardingStep[] = [
      'kickoff',
      'gmail_setup',
      'property_import',
      'tenant_import',
      'training',
      'testing',
      'go_live',
      'follow_up',
    ];

    return steps.map((step) => {
      const stepStats = stats.stepStats[step];
      return {
        step,
        name: getStepDisplayName(step),
        completionRate: stepStats?.completionRate || 0,
        avgDuration: stepStats?.averageDuration || 0,
        blockers: stepStats?.blockerCount || 0,
      };
    });
  };

  const funnelData = getFunnelData();

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Onboarding Metrics</Heading>
        <Select w="200px" value={dateRange} onChange={(e) => setDateRange(e.target.value as any)}>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="all">All time</option>
        </Select>
      </Flex>

      {/* Key Metrics */}
      <Grid templateColumns="repeat(6, 1fr)" gap={6} mb={6}>
        {/* Total Onboardings */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Total</StatLabel>
              <StatNumber>{stats.total}</StatNumber>
              <StatHelpText>
                <VStack spacing={0} align="start">
                  <Text fontSize="xs">{stats.completed} completed</Text>
                  <Text fontSize="xs">{stats.inProgress} active</Text>
                </VStack>
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Average Time-to-Value */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Avg Time-to-Value</StatLabel>
              <StatNumber fontSize="xl">
                {stats.averageTimeToValue > 0
                  ? `${Math.round(stats.averageTimeToValue)}d`
                  : 'N/A'}
              </StatNumber>
              <StatHelpText>
                <HStack spacing={1}>
                  <Icon
                    as={stats.averageTimeToValue <= 21 ? MdTrendingDown : MdTrendingUp}
                    color={stats.averageTimeToValue <= 21 ? 'green.500' : 'orange.500'}
                  />
                  <Text fontSize="xs">Target: &lt;21d</Text>
                </HStack>
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* On-Time Completion */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>On-Time Rate</StatLabel>
              <StatNumber fontSize="xl">
                {stats.completed > 0 ? `${Math.round(stats.onTimeCompletionRate)}%` : 'N/A'}
              </StatNumber>
              <StatHelpText>
                <HStack spacing={1}>
                  <Icon
                    as={stats.onTimeCompletionRate >= 80 ? MdCheckCircle : MdWarning}
                    color={stats.onTimeCompletionRate >= 80 ? 'green.500' : 'orange.500'}
                  />
                  <Text fontSize="xs">Target: &gt;80%</Text>
                </HStack>
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Average NPS */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Avg NPS</StatLabel>
              <StatNumber fontSize="xl">
                {stats.averageNPS > 0 ? `${Math.round(stats.averageNPS)}/10` : 'N/A'}
              </StatNumber>
              <StatHelpText>
                <HStack spacing={1}>
                  <Icon
                    as={npsTrend > 0 ? MdTrendingUp : npsTrend < 0 ? MdTrendingDown : MdStar}
                    color={npsTrend > 0 ? 'green.500' : npsTrend < 0 ? 'red.500' : 'gray.500'}
                  />
                  <Text fontSize="xs">{stats.npsPromoters} promoters</Text>
                </HStack>
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Average Satisfaction */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Satisfaction</StatLabel>
              <StatNumber fontSize="xl">
                {stats.averageSatisfaction > 0
                  ? `${stats.averageSatisfaction.toFixed(1)}/5`
                  : 'N/A'}
              </StatNumber>
              <StatHelpText>
                <HStack spacing={1}>
                  <Icon
                    as={MdStar}
                    color={
                      stats.averageSatisfaction >= 4.5
                        ? 'green.500'
                        : stats.averageSatisfaction >= 4
                        ? 'yellow.500'
                        : 'orange.500'
                    }
                  />
                  <Text fontSize="xs">
                    {Math.round((stats.averageSatisfaction / 5) * 100)}%
                  </Text>
                </HStack>
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Active Blockers */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Active Blockers</StatLabel>
              <StatNumber fontSize="xl" color={stats.activeBlockers > 0 ? 'red.500' : 'gray.500'}>
                {stats.activeBlockers}
              </StatNumber>
              <StatHelpText>
                <HStack spacing={1}>
                  <Icon
                    as={blockerTrend >= 0 ? MdTrendingDown : MdTrendingUp}
                    color={blockerTrend >= 0 ? 'green.500' : 'red.500'}
                  />
                  <Text fontSize="xs">{stats.blockersBySeverity.critical} critical</Text>
                </HStack>
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </Grid>

      {/* Charts Row */}
      <Grid templateColumns="2fr 1fr" gap={6} mb={6}>
        {/* Onboarding Funnel */}
        <Card>
          <CardHeader>
            <Heading size="md">Onboarding Funnel</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              {funnelData.map((step, index) => {
                const maxWidth = 100;
                const width = (step.completionRate / 100) * maxWidth;

                return (
                  <Box key={step.step}>
                    <HStack justify="space-between" mb={1}>
                      <HStack spacing={2}>
                        <Badge colorScheme="blue" fontSize="xs">
                          {index + 1}
                        </Badge>
                        <Text fontSize="sm" fontWeight="medium">
                          {step.name}
                        </Text>
                      </HStack>
                      <HStack spacing={4}>
                        <Text fontSize="sm" color="gray.600">
                          {Math.round(step.completionRate)}% complete
                        </Text>
                        {step.avgDuration > 0 && (
                          <Text fontSize="sm" color="gray.600">
                            {Math.round(step.avgDuration)}d avg
                          </Text>
                        )}
                        {step.blockers > 0 && (
                          <Badge colorScheme="red" fontSize="xs">
                            {step.blockers} blockers
                          </Badge>
                        )}
                      </HStack>
                    </HStack>
                    <Box bg="gray.100" h="30px" borderRadius="md" overflow="hidden">
                      <Box
                        bg={
                          step.completionRate >= 90
                            ? 'green.400'
                            : step.completionRate >= 70
                            ? 'blue.400'
                            : step.completionRate >= 50
                            ? 'yellow.400'
                            : 'red.400'
                        }
                        h="100%"
                        w={`${width}%`}
                        transition="width 0.3s"
                      />
                    </Box>
                  </Box>
                );
              })}
            </VStack>
          </CardBody>
        </Card>

        {/* Time-to-Value Distribution */}
        <Card>
          <CardHeader>
            <Heading size="md">Time-to-Value</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              {timeToValueDistribution.map((range) => {
                const total = timeToValueDistribution.reduce((sum, r) => sum + r.count, 0);
                const percentage = total > 0 ? (range.count / total) * 100 : 0;

                return (
                  <Box key={range.label}>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="sm" fontWeight="medium">
                        {range.label}
                      </Text>
                      <HStack spacing={2}>
                        <Text fontSize="sm" color="gray.600">
                          {range.count}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          ({Math.round(percentage)}%)
                        </Text>
                      </HStack>
                    </HStack>
                    <Progress
                      value={percentage}
                      colorScheme={range.color}
                      size="sm"
                      borderRadius="md"
                    />
                  </Box>
                );
              })}
            </VStack>
          </CardBody>
        </Card>
      </Grid>

      {/* NPS Breakdown */}
      <Card mb={6}>
        <CardHeader>
          <Heading size="md">NPS Breakdown</Heading>
        </CardHeader>
        <CardBody>
          <Grid templateColumns="repeat(4, 1fr)" gap={6}>
            <Stat>
              <StatLabel>Promoters (9-10)</StatLabel>
              <StatNumber color="green.500">{stats.npsPromoters}</StatNumber>
              <StatHelpText>
                {stats.npsPromoters + stats.npsPassives + stats.npsDetractors > 0 &&
                  `${Math.round(
                    (stats.npsPromoters /
                      (stats.npsPromoters + stats.npsPassives + stats.npsDetractors)) *
                      100
                  )}%`}
              </StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Passives (7-8)</StatLabel>
              <StatNumber color="yellow.500">{stats.npsPassives}</StatNumber>
              <StatHelpText>
                {stats.npsPromoters + stats.npsPassives + stats.npsDetractors > 0 &&
                  `${Math.round(
                    (stats.npsPassives /
                      (stats.npsPromoters + stats.npsPassives + stats.npsDetractors)) *
                      100
                  )}%`}
              </StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Detractors (0-6)</StatLabel>
              <StatNumber color="red.500">{stats.npsDetractors}</StatNumber>
              <StatHelpText>
                {stats.npsPromoters + stats.npsPassives + stats.npsDetractors > 0 &&
                  `${Math.round(
                    (stats.npsDetractors /
                      (stats.npsPromoters + stats.npsPassives + stats.npsDetractors)) *
                      100
                  )}%`}
              </StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Response Rate</StatLabel>
              <StatNumber>
                {stats.npsResponseRate > 0 ? `${Math.round(stats.npsResponseRate)}%` : 'N/A'}
              </StatNumber>
              <StatHelpText>
                {stats.npsPromoters + stats.npsPassives + stats.npsDetractors} responses
              </StatHelpText>
            </Stat>
          </Grid>
        </CardBody>
      </Card>

      {/* Specialist Performance */}
      <Card mb={6}>
        <CardHeader>
          <Heading size="md">Specialist Performance</Heading>
        </CardHeader>
        <CardBody>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Specialist</Th>
                <Th isNumeric>Active</Th>
                <Th isNumeric>Completed</Th>
                <Th isNumeric>Avg NPS</Th>
                <Th isNumeric>Avg Time-to-Value</Th>
                <Th>Performance</Th>
              </Tr>
            </Thead>
            <Tbody>
              {Object.keys(stats.bySpecialist).length === 0 ? (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={8}>
                    <Text color="gray.500">No specialist data available</Text>
                  </Td>
                </Tr>
              ) : (
                Object.entries(stats.bySpecialist).map(([userId, specialist]) => {
                  const performanceScore =
                    (specialist.averageNPS / 10) * 0.5 +
                    (specialist.averageTimeToValue <= 21 ? 0.5 : 0.3);
                  const performanceLabel =
                    performanceScore >= 0.8
                      ? 'Excellent'
                      : performanceScore >= 0.6
                      ? 'Good'
                      : 'Needs Improvement';
                  const performanceColor =
                    performanceScore >= 0.8 ? 'green' : performanceScore >= 0.6 ? 'blue' : 'orange';

                  return (
                    <Tr key={userId}>
                      <Td>
                        <HStack spacing={2}>
                          <Icon as={MdPeople} color="blue.500" />
                          <Text fontWeight="medium">{specialist.name}</Text>
                        </HStack>
                      </Td>
                      <Td isNumeric>{specialist.activeCount}</Td>
                      <Td isNumeric>{specialist.completedCount}</Td>
                      <Td isNumeric>
                        {specialist.averageNPS > 0 ? (
                          <Badge
                            colorScheme={
                              specialist.averageNPS >= 9
                                ? 'green'
                                : specialist.averageNPS >= 7
                                ? 'yellow'
                                : 'red'
                            }
                          >
                            {specialist.averageNPS.toFixed(1)}/10
                          </Badge>
                        ) : (
                          <Text color="gray.400">N/A</Text>
                        )}
                      </Td>
                      <Td isNumeric>
                        {specialist.averageTimeToValue > 0 ? (
                          <Badge
                            colorScheme={specialist.averageTimeToValue <= 21 ? 'green' : 'orange'}
                          >
                            {Math.round(specialist.averageTimeToValue)}d
                          </Badge>
                        ) : (
                          <Text color="gray.400">N/A</Text>
                        )}
                      </Td>
                      <Td>
                        <Badge colorScheme={performanceColor}>{performanceLabel}</Badge>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      {/* Blocker Analysis */}
      <Grid templateColumns="1fr 1fr" gap={6}>
        {/* Blockers by Step */}
        <Card>
          <CardHeader>
            <Heading size="md">Blockers by Step</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={3} align="stretch">
              {Object.entries(stats.blockersByStep).map(([step, count]) => {
                const maxBlockers = Math.max(...Object.values(stats.blockersByStep));
                const percentage = maxBlockers > 0 ? (count / maxBlockers) * 100 : 0;

                return (
                  <Box key={step}>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="sm" fontWeight="medium">
                        {getStepDisplayName(step as OnboardingStep)}
                      </Text>
                      <Badge colorScheme={count > 0 ? 'red' : 'gray'}>{count}</Badge>
                    </HStack>
                    <Progress
                      value={percentage}
                      colorScheme={count > 5 ? 'red' : count > 2 ? 'orange' : 'yellow'}
                      size="sm"
                      borderRadius="md"
                    />
                  </Box>
                );
              })}
            </VStack>
          </CardBody>
        </Card>

        {/* Blockers by Severity */}
        <Card>
          <CardHeader>
            <Heading size="md">Blockers by Severity</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={2} spacing={4}>
                <Stat>
                  <StatLabel>Critical</StatLabel>
                  <StatNumber color="red.500">{stats.blockersBySeverity.critical}</StatNumber>
                  <Progress
                    value={
                      stats.activeBlockers > 0
                        ? (stats.blockersBySeverity.critical / stats.activeBlockers) * 100
                        : 0
                    }
                    colorScheme="red"
                    size="sm"
                    mt={2}
                  />
                </Stat>
                <Stat>
                  <StatLabel>High</StatLabel>
                  <StatNumber color="orange.500">{stats.blockersBySeverity.high}</StatNumber>
                  <Progress
                    value={
                      stats.activeBlockers > 0
                        ? (stats.blockersBySeverity.high / stats.activeBlockers) * 100
                        : 0
                    }
                    colorScheme="orange"
                    size="sm"
                    mt={2}
                  />
                </Stat>
                <Stat>
                  <StatLabel>Medium</StatLabel>
                  <StatNumber color="yellow.500">{stats.blockersBySeverity.medium}</StatNumber>
                  <Progress
                    value={
                      stats.activeBlockers > 0
                        ? (stats.blockersBySeverity.medium / stats.activeBlockers) * 100
                        : 0
                    }
                    colorScheme="yellow"
                    size="sm"
                    mt={2}
                  />
                </Stat>
                <Stat>
                  <StatLabel>Low</StatLabel>
                  <StatNumber color="blue.500">{stats.blockersBySeverity.low}</StatNumber>
                  <Progress
                    value={
                      stats.activeBlockers > 0
                        ? (stats.blockersBySeverity.low / stats.activeBlockers) * 100
                        : 0
                    }
                    colorScheme="blue"
                    size="sm"
                    mt={2}
                  />
                </Stat>
              </SimpleGrid>
              <Box pt={4} borderTop="1px" borderColor="gray.200">
                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="medium">
                    Total Active Blockers
                  </Text>
                  <Badge colorScheme="red" fontSize="md">
                    {stats.activeBlockers}
                  </Badge>
                </HStack>
                <HStack justify="space-between" mt={2}>
                  <Text fontSize="sm" color="gray.600">
                    Resolved Blockers
                  </Text>
                  <Badge colorScheme="green" fontSize="md">
                    {stats.resolvedBlockers}
                  </Badge>
                </HStack>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </Grid>
    </Box>
  );
}
